import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "flowbite-react";
import { Vehicle, Parameter } from "../../models";
import { NorwayRectangle, emptyVehicleIllustration } from "@components";
import { CatalogCategoryGrid } from "../Catalog/CatalogCategoryGrid";
import { cn, panelInteractive } from "@design-system";

interface VehicleCategoriesProps {
  vehicles: Vehicle[];
  categories: Parameter[];
  isLoading: boolean;
  onSelectCategory: (categoryId: string | null) => void;
  onSelectVehicle: (vehicle: Vehicle) => void;
  selectedCategoryId: string | null;
  onAddCategory?: () => void;
  onDeleteCategory?: (categoryId: string) => void;
}

// Separate component for vehicle card to improve readability and reusability
interface VehicleCardProps {
  vehicle: Vehicle;
  categories: Parameter[];
  onClick: () => void;
  t: any;
}

const VehicleCard: FC<VehicleCardProps> = ({
  vehicle,
  categories,
  onClick,
  t,
}) => {
  // Find category name
  const categoryName = useMemo(() => {
    if (!vehicle.categoryCode) {
      return (
        <Badge key="uncategorized" color="gray" className="text-xs">
          {t("vehicles:categories.uncategorized")}
        </Badge>
      );
    }

    const category = categories.find((c) => c.code === vehicle.categoryCode);
    return category ? (
      <Badge key={category.id} color="purple" className="text-xs">
        {category.code}
      </Badge>
    ) : (
      <Badge key="unknown" color="gray" className="text-xs">
        {vehicle.categoryCode}
      </Badge>
    );
  }, [vehicle, categories, t]);

  // Format plate number with space after first two characters
  const plateNumber = useMemo(() => {
    const upperPlate = vehicle.registrationNumber.toUpperCase();
    return upperPlate.length > 2
      ? upperPlate.slice(0, 2) + " " + upperPlate.slice(2)
      : upperPlate;
  }, [vehicle.registrationNumber]);

  return (
    <div
      className={cn(panelInteractive, "cursor-pointer p-3")}
      onClick={onClick}
    >
      <div className="flex items-center mb-2">
        {/* License Plate - smaller version */}
        <div
          className="flex items-center bg-surface border border-border rounded overflow-hidden"
          style={{ height: "30px" }}
        >
          {/* Blue side panel with the Norwegian flag and "N" */}
          <div
            className="flex flex-col items-center justify-center bg-blue-700 p-0.5"
            style={{ width: "30px", height: "30px" }}
          >
            <NorwayRectangle />
            <span className="text-white text-[8px] font-bold">N</span>
          </div>

          {/* Main plate area with the registration number */}
          <div className="flex items-center px-2">
            <p className="text-sm font-medium dark:text-text">
              {plateNumber}
            </p>
          </div>
        </div>
      </div>

      {/* Vehicle description */}
      <p className="text-sm text-muted line-clamp-2 mb-2">
        {vehicle.name || t("vehicles:no_description")}
      </p>

      <div className="flex flex-wrap gap-2 mt-1">{categoryName}</div>
    </div>
  );
};

export const VehicleCategories: FC<VehicleCategoriesProps> = ({
  vehicles,
  categories,
  isLoading,
  onSelectCategory,
  onSelectVehicle,
  selectedCategoryId,
  onAddCategory,
  onDeleteCategory,
}) => {
  const { t } = useTranslation(["common", "vehicles"]);

  return (
    <CatalogCategoryGrid<Vehicle>
      items={vehicles}
      categories={categories}
      isLoading={isLoading}
      selectedCategoryId={selectedCategoryId}
      onSelectCategory={onSelectCategory}
      onSelectItem={onSelectVehicle}
      onAddCategory={onAddCategory}
      onDeleteCategory={onDeleteCategory}
      t={t}
      namespace="vehicles"
      labels={{
        allLabel: t("vehicles:categories.all_vehicles"),
        noItems: {
          title: t("vehicles:no_vehicles.title"),
          description: t("vehicles:no_vehicles.description"),
          buttonText: t("vehicles:no_vehicles.button_text"),
        },
      }}
      searchInputId="vehicleSearch"
      emptyIllustration={emptyVehicleIllustration}
      getCategoryIdForItem={(vehicle, cats) => {
        if (!vehicle.categoryCode) return null;
        const category = cats.find((c) => c.code === vehicle.categoryCode);
        return category ? category.id : null;
      }}
      matchesSearch={(vehicle, searchLower) =>
        vehicle.name.toLowerCase().includes(searchLower) ||
        vehicle.registrationNumber.toLowerCase().includes(searchLower) ||
        (!!vehicle.categoryCode &&
          vehicle.categoryCode.toLowerCase().includes(searchLower))
      }
      renderCard={(vehicle) => (
        <VehicleCard
          key={vehicle.id}
          vehicle={vehicle}
          categories={categories}
          onClick={() => onSelectVehicle(vehicle)}
          t={t}
        />
      )}
      createDefaultItem={(selId) => {
        const newVehicle = Vehicle.default();
        if (selId && selId !== "uncategorized") {
          const category = categories.find((c) => c.id === selId);
          if (category) {
            newVehicle.categoryCode = category.code;
          }
        }
        return newVehicle;
      }}
      alwaysShowSearch={false}
      showNoSearchResults={false}
    />
  );
};
